import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import type { DomainHealthCheck, DomainHealthSummary } from "../_types";

export function useDomainHealth() {
  const [data, setData] = useState<DomainHealthSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState<string | null>(null);
  const [checkingAll, setCheckingAll] = useState(false);

  const fetchHealth = useCallback(async () => {
    try {
      const response = await api.domainHealth.list();
      setData(response);
    } catch (err) {
      toast.error("Failed to load domain health data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
  }, [fetchHealth]);

  const checkDomain = useCallback(async (domainId: string) => {
    setChecking(domainId);
    try {
      const result = await api.domainHealth.check(domainId);
      setData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          domains: prev.domains.map((d) =>
            d.domainId === domainId ? result : d
          ),
          summary: {
            ...prev.summary,
            healthy: prev.domains.filter(d =>
              d.domainId === domainId ? result.status === "healthy" : d.status === "healthy"
            ).length,
            warning: prev.domains.filter(d =>
              d.domainId === domainId ? result.status === "warning" : d.status === "warning"
            ).length,
            blocked: prev.domains.filter(d =>
              d.domainId === domainId ? result.status === "blocked" : d.status === "blocked"
            ).length,
            unknown: prev.domains.filter(d =>
              d.domainId === domainId ? result.status === "unknown" : d.status === "unknown"
            ).length,
          },
        };
      });
      toast.success(`Health check complete for ${result.domain}`, {
        description: `Score: ${result.score}/100 - ${result.status}`,
      });
    } catch (err) {
      toast.error("Failed to check domain health");
    } finally {
      setChecking(null);
    }
  }, []);

  const checkAllDomains = useCallback(async () => {
    setCheckingAll(true);
    try {
      const result = await api.domainHealth.checkAll();
      setData(result);
      toast.success("All domains checked", {
        description: `${result.summary.healthy} healthy, ${result.summary.warning} warning, ${result.summary.blocked} blocked`,
      });
    } catch (err) {
      toast.error("Failed to check all domains");
    } finally {
      setCheckingAll(false);
    }
  }, []);

  return {
    data,
    loading,
    checking,
    checkingAll,
    checkDomain,
    checkAllDomains,
    refresh: fetchHealth,
  };
}
